import React, { useEffect } from 'react';
import { ArrowRight, Database, FileText, Share2, Search, BookOpen, Activity, Brain, Dna, Layers, TrendingUp } from 'lucide-react';
import UnifiedSearch from './UnifiedSearch';
import SynapticCompartmentsSection from './SynapticCompartmentsSection';
import { Link, useLocation } from 'react-router-dom';
import CookiesModal from '../components/CookiesModal';
import ChapterCards from '../components/ChapterCards';
import TotalApiVisitsCard from '../components/TotalApiVisitsCard';
import TotalVisitsCard from '../components/TotalVisitsCard';
import SynapseProteomesSection from './SynapseProteomesSection';
import ContinuousGradientTitle from '../components/ContinuousGradientTitle';

const LandingPage = () => {
  useEffect(() => {
    document.title = 'SynaptopathyDB - A Comprehensive Resource for Synaptopathy Research';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <CookiesModal />
      {/* Hero Section with Combined Search */}
      <header className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="text-center">
          {/* <h1 className="text-4xl font-bold pb-3 tracking-tight text-gray-900 sm:text-5xl md:text-6xl bg-gradient-to-r from-blue-600 via-purple-700 to-indigo-600 text-transparent bg-clip-text">
            SynaptopathyDB
          </h1> */}
          <ContinuousGradientTitle/>
          {/* <p className="max-w-md mx-auto mt-3 text-base text-gray-800 font-semibold sm:text-lg md:mt-5 md:text-2xl md:max-w-3xl">
            A Comprehensive Resource for Synaptopathy Research
          </p> */}
          <p className="max-w-md mx-auto mt-3 text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-4xl">
            Connecting synapse proteome with human genetic, disease and phenotype data.
          </p>

          {/* Unified Search Component */}
          <div className="mt-8 mb-10">
            <UnifiedSearch />
          </div>

          {/* Quick Access Buttons */}
          <div className="max-w-5xl mx-auto mt-5 grid sm:grid-cols-3 gap-6">
            <div className="flex flex-col items-center">
              <div className="rounded-md shadow w-full">
                <a
                  href="/gene-search"
                  className="flex flex-col items-center justify-center w-full px-6 py-3 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 md:py-4 md:px-8"
                >
                  <div className="flex items-center justify-center mb-2">
                    <span className="font-medium text-xl">Search Genes</span>
                    <Dna className="w-5 h-5 ml-2" />
                  </div>
                  <p className="text-xs text-center text-blue-100">
                    Find and explore gene information and related pathways
                  </p>
                </a>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <div className="rounded-md shadow w-full">
                <a
                  href="/disease-search"
                  className="flex flex-col items-center justify-center w-full px-6 py-3 text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 md:py-4 md:px-8"
                >
                  <div className="flex items-center justify-center mb-2">
                    <span className="font-medium text-xl">Search Diseases</span>
                    <Activity className="w-5 h-5 ml-2" />
                  </div>
                  <p className="text-xs text-center text-purple-100">
                    Discover disease mechanisms and associated genetic factors
                  </p>
                </a>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <div className="rounded-md shadow w-full">
                <a
                  href="/paper-search"
                  className="flex flex-col items-center justify-center w-full px-6 py-3 text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 md:py-4 md:px-8"
                >
                  <div className="flex items-center justify-center mb-2">
                    <span className="font-medium text-xl">Search Publications</span>
                    <BookOpen className="w-5 h-5 ml-2" />
                  </div>
                  <p className="text-xs text-center text-indigo-100">
                    Search for synapse proteomics studies exploring gene-mutation interactions
                  </p>
                </a>
              </div>
            </div>
          </div>

          {/* Catalog Links */}
          <div className="max-w-5xl mx-auto mt-12">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center flex items-center justify-center">
                <Layers className="w-5 h-5 mr-2 text-blue-600" />
                Browse SynaptopathyDB Catalogs
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <a
                  href="/gene-table"
                  className="group flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-center">
                    <div className="bg-blue-50 p-2 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <Dna className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="font-medium text-gray-900 group-hover:text-blue-600">Synapse Proteome Catalog</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </a>

                <a
                  href="/disease-table"
                  className="group flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-lg hover:border-purple-400 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-center">
                    <div className="bg-purple-50 p-2 rounded-lg group-hover:bg-purple-100 transition-colors">
                      <Activity className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="font-medium text-gray-900 group-hover:text-purple-600">Diseases Catalog</p>
                      {/* <p className="text-xs text-gray-500">All classified diseases</p> */}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
                </a>
                <a

                  href="/paper-table"
                  className="group flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-center">
                    <div className="bg-indigo-50 p-2 rounded-lg group-hover:bg-indigo-100 transition-colors">
                      <BookOpen className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="ml-4">
                      <p className="font-medium text-gray-900 group-hover:text-indigo-600">Publications Catalog</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
                </a>
              </div>
            </div>
          </div>


        </div>
      </header>

      {/* Overview Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Overview</h2>
            <p className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl">
              Connecting Synaptic Proteins to Brain Disorders
            </p>
          </div>

          <div className="mt-10 prose prose-blue prose-lg text-gray-700 max-w-4xl mx-auto">


            <p>Synaptic dysfunction resulting from mutations in genes encoding synaptic proteins is as a major contributor to brain and behavioral disorders, collectively termed synaptopathies. To facilitate research into the genetic basis and clinical manifestations of synaptopathy, we have created SynaptopathyDB which integrates data from 64 mammalian synapse proteomic studies with multiple genetic and phenotypic resources. We identified a consensus set of 3,437 mammalian synapse proteins and found 1,236 human genes (36%) were associated with 347 disorders of the nervous system and 947 nervous system phenotypes.</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Key Features</h2>
            <p className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl">
              Comprehensive Synaptopathy Research Platform
            </p>
          </div>

          <div className="mt-10">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-10">
              <div className="relative">
                <div className="flex items-center justify-center w-12 h-12 rounded-md bg-blue-500 text-white">
                  <Database className="w-6 h-6" />
                </div>
                <div className="mt-5">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Consensus Synapse Proteins</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Access a curated database of 3,437 synapse proteins reported in five or more studies to reduce potential contaminants.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="flex items-center justify-center w-12 h-12 rounded-md bg-blue-500 text-white">
                  <FileText className="w-6 h-6" />
                </div>
                <div className="mt-5">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Comprehensive Disease Mapping</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Explore 1,236 synapse genes with mutations associated with 347 central and peripheral nervous system disorders.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="flex items-center justify-center w-12 h-12 rounded-md bg-blue-500 text-white">
                  <Share2 className="w-6 h-6" />
                </div>
                <div className="mt-5">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Intuitive Search Interfaces</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Use our powerful Gene Search, Disease Search, and Search Synapse Proteomic Publications options with detailed filtering and visualization tools.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Synapse Compartments Section */}
      {/* <SynapticCompartmentsSection /> */}
      <SynapseProteomesSection />

      {/* Disease Classification Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-10">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">International Classification of Diseases</h2>
            <p className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl">
              Synaptopathies Across Body Systems
            </p>
          </div>

          <ChapterCards />
        </div>
      </section>

      {/* Search Options Section */}
      {/* <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-10">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Search Options</h2>
            <p className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl">
              Multiple Ways to Explore
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                    <Dna className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Gene Search</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Search by gene name, ID, or Entrez to discover mutations, associated publications, and disease links.
                    </p>
                  </div>
                </div>
                <div className="mt-5">
                  <a
                    href="/gene-search"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Go to Gene Search
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                    <Activity className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Disease Search</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Explore 347 nervous system disorders and their associations with synaptic gene mutations.
                    </p>
                  </div>
                </div>
                <div className="mt-5">
                  <a
                    href="/disease-search"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700"
                  >
                    Go to Disease Search
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                    <BookOpen className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Search Publications</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Find research from the 58 integrated synapse proteome studies that form the foundation of our database.
                    </p>
                  </div>
                </div>
                <div className="mt-5">
                  <a
                    href="/paper-search"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Go to Publication Search
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </section> */}


      <section className="py-16 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Research Team</h2>
          </div>

          <div className="flex flex-wrap justify-center">
            {[
              "Oksana Sorokina",
              "Digin Dominic",
              "Àlex Bayés",
              "J. Douglas Armstrong",
              "Seth G.N. Grant"
            ].map((name, index) => (
              <div
                key={index}
                className="w-1/2 md:w-1/3 p-3"
              >
                <div className="bg-white rounded-lg p-6 border-l-4 border-blue-500 shadow-md hover:shadow-lg transition-all">
                  <p className="font-medium text-gray-800 text-center">{name}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href="/about#research-team"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Learn more about our team
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Funders Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Funding Partners</h2>
            <p className="mt-2 text-lg text-gray-600">
              This research is supported by generous funding from
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <div className="w-50 h-50 flex items-center justify-center">
              <a href="https://wellcome.org" target="_blank" rel="noopener noreferrer">
                <img
                  src="/funders/wellcome-logo-black.jpg"
                  alt="Wellcome Trust"
                  className="max-h-full object-contain"
                />
              </a>
            </div>
            <div className="w-100 h-100 flex items-center justify-center">
              <a href="https://erc.europa.eu" target="_blank" rel="noopener noreferrer">
                <img
                  src="/funders/LOGO_ERC-FLAG_EU_.jpg"
                  alt="European Research Council and European Union"
                  className="max-h-full object-contain"
                />
              </a>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-gray-500">
              This project has received funding from the Wellcome Trust and the European Research Council (ERC)
              under the European Union's research and innovation programme.
            </p>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-center gap-1 md:gap-1 mt-16">
            <div className="flex items-center justify-center">
              <a href="https://sidb.org.uk/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/funders/sidb.png"
                  alt="Simons Initiative for the Developing Brain"
                  className="w-80 h-auto object-contain"
                />
              </a>
            </div>
            <div className="flex items-center justify-center">
              <a href="https://www.ed.ac.uk/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/funders/uoe.png"
                  alt="University of Edinburgh"
                  className="w-80 h-auto object-contain"
                />
              </a>
            </div>
            <div className="flex items-center justify-center">
              <a href="https://www.ukri.org/councils/bbsrc/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/funders/bbrc.png"
                  alt="Biotechnology and Biological Sciences Research Council (BBSRC)"
                  className="w-90 h-auto object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to explore synaptopathies?</span>
            <span className="block text-blue-200">Start exploring 1,236 synapse genes linked to 347 disorders.</span>
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <a
                href="/about"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50"
              >
                Learn More
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>
      <div className="mt-10 flex justify-center space-x-4">
          <Link to="/api-counter-dashboard" className="w-full max-w-md cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1">
            <TotalApiVisitsCard className="h-full" />
          </Link>
          <TotalVisitsCard className="h-full" />
        </div>

    </div>
  );
};

export default LandingPage;